import React from "react";

interface BulletPoint {
  point: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  bullets?: string | BulletPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultBullets = [
  {
    point: "Light Absorption",
    description: "Chlorophyll molecules capture sunlight energy",
    icon: "☀️",
  },
  {
    point: "Water Splitting",
    description: "H2O molecules split into H+ and O2",
    icon: "💧",
  },
  {
    point: "Electron Transport",
    description: "Electrons flow through photosystems",
    icon: "⚡",
  },
  {
    point: "ATP Production",
    description: "Chemical energy is stored as ATP",
    icon: "🔋",
  },
  {
    point: "Carbon Fixation",
    description: "CO2 is converted to glucose",
    icon: "🌱",
  },
  {
    point: "Oxygen Release",
    description: "O2 is released as a byproduct",
    icon: "🌬️",
  },
];

const BulletSlide1 = {
  id: "bullet-slide1",
  name: "Bullet Points (Modern Grid)",
  category: "Bullet Points",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Key Stages of Photosynthesis",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "Understanding the Process Step by Step",
    },
    {
      id: "bullets",
      label: "Bullets (JSON array of { point, description, icon })",
      type: "text",
      defaultValue: JSON.stringify(defaultBullets, null, 2),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #15803d 0%, #16a34a 50%, #22c55e 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "Key Stages of Photosynthesis",
      subtitle = "Understanding the Process Step by Step",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #15803d 0%, #16a34a 50%, #22c55e 100%)",
      bullets = JSON.stringify(defaultBullets),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleBulletEdit = (index: number, field: string, value: string) => {
      let parsedBullets = defaultBullets;
      try {
        if (typeof bullets === "string") {
          parsedBullets = JSON.parse(bullets);
        }
      } catch (error) {
        console.warn("Error parsing bullets:", error);
      }

      const updatedBullets = [...parsedBullets];
      updatedBullets[index] = {
        ...updatedBullets[index],
        [field]: value,
      };

      handleEdit("bullets")(JSON.stringify(updatedBullets));
    };

    let parsedBullets = defaultBullets;
    try {
      if (typeof bullets === "string") {
        const parsed = JSON.parse(bullets);
        if (Array.isArray(parsed) && parsed.length > 0) {
          parsedBullets = parsed;
        }
      } else if (Array.isArray(bullets) && bullets.length > 0) {
        parsedBullets = bullets as BulletPoint[];
      }
    } catch (error) {
      console.warn("Error parsing bullets, using defaults:", error);
    }

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute transform rotate-45 bg-white/5"
                style={{
                  width: "120px",
                  height: "120px",
                  left: `${(i % 5) * 25}%`,
                  top: `${Math.floor(i / 5) * 25}%`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleEdit("title")(e.currentTarget.textContent || "")
              }
              className="text-4xl font-bold mb-2 text-green-100"
            >
              {title}
            </div>
            <div
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                handleEdit("subtitle")(e.currentTarget.textContent || "")
              }
              className="text-xl font-light text-green-100/90"
            >
              {subtitle}
            </div>
            <div className="w-32 h-1 mx-auto mt-4 bg-gradient-to-r from-green-300/50 to-transparent" />
          </div>

          {/* Bullets Grid */}
          <div className="flex-1 grid grid-cols-2 gap-8 mb-8 px-4">
            {parsedBullets.map((bullet, idx) => (
              <div
                key={idx}
                className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-8
                          transform transition-all duration-300 hover:scale-102 hover:bg-white/20"
              >
                {/* Bullet Number */}
                <div
                  className="absolute -top-4 -left-4 w-10 h-10 bg-green-600 rounded-full 
                              flex items-center justify-center text-xl font-bold shadow-lg"
                >
                  {idx + 1}
                </div>

                {/* Icon */}
                <div
                  className="absolute -top-4 -right-4 w-10 h-10 bg-green-600/80 rounded-lg 
                              flex items-center justify-center text-2xl transform rotate-12 
                              group-hover:rotate-0 transition-transform duration-300"
                >
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "icon",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="w-full h-full flex items-center justify-center"
                  >
                    {bullet.icon}
                  </div>
                </div>

                {/* Content */}
                <div className="mt-6">
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "point",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="text-2xl font-bold text-green-100 mb-4"
                  >
                    {bullet.point}
                  </div>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBulletEdit(
                        idx,
                        "description",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className="text-base text-green-100/80 leading-relaxed max-h-[120px] overflow-y-auto 
                              scrollbar-thin scrollbar-track-green-900/20 scrollbar-thumb-green-600/50"
                  >
                    {bullet.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default BulletSlide1;
